import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useTripStore } from '@apps/main/new-desktop/core/stores';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import type {
  AvailableHotelIngredientResponseWithStaticInfo,
  Money,
} from '@libs/trip-planner-interfaces';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import { isAvailableHotelIngredientResponseWithStaticInfo } from '@libs/search-service-smart-search';

import type {
  ActivityStaticData,
  ActivityDynamicData,
} from '../../activityDetails';
import type { ActivityDetailsGalleryData, Distance } from '../models';
import type { ActivityDetailsMainData } from '../models/activityDetailsMain';
import type {
  SelectedActivityTicketsInfo,
  SelectedTickets,
} from '../models/activityDetailsSummary';

import { getDistances } from './activityDetailsStoreLogic';

export const useActivityDetailsStore = defineStore(
  'activityDetailsStore',
  () => {
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();

    // State
    const activityStaticData = ref<ActivityStaticData | undefined>(undefined);
    const activityDynamicData = ref<ActivityDynamicData | undefined>(undefined);

    // Actions
    function setActivityStaticData(
      initialActivityStaticData: ActivityStaticData
    ): void {
      activityStaticData.value = initialActivityStaticData;
    }

    function setActivityDynamicData(
      initialActivityDynamicData: ActivityDynamicData
    ): void {
      activityDynamicData.value = initialActivityDynamicData;
    }

    // Getters
    const isLoading = computed(
      () =>
        !activityStaticData.value ||
        !activityDynamicData.value ||
        isFeatureToggleActive('loader')
    );

    const isLoadingTickets = computed(
      () =>
        isFeatureToggleActive('loader') ||
        !activityDynamicData.value ||
        activityDynamicData.value.isAvailabilityLoading ||
        !activityDynamicData.value.availability.length
    );

    const destinationCity = computed<string | undefined>(
      () =>
        activityStaticData.value?.staticInfo.mainPolygon?.fullName.split(',')[0]
    );

    const galleryData = computed<ActivityDetailsGalleryData>(() => ({
      images: activityStaticData.value?.staticInfo.images,
      productName: activityStaticData.value?.staticInfo.name,
      productCode: activityStaticData.value?.productCode,
    }));

    const mainData = computed<ActivityDetailsMainData>(() => ({
      activityName: activityStaticData.value?.staticInfo.name,
      fullDestination:
        activityStaticData.value?.staticInfo.mainPolygon?.fullName,
      rating: activityStaticData.value?.staticInfo.semboSunRating,
      mainDescription:
        activityStaticData.value?.staticInfo.openingParagraph.value,
    }));

    const description = computed<string | undefined>(() => {
      const description =
        activityStaticData.value?.staticInfo?.description?.value;
      if (!description) {
        return;
      }
      let formattedDescription = description;
      if (mainData.value.mainDescription) {
        formattedDescription = description.replace(
          mainData.value.mainDescription,
          ''
        );
      }
      return formattedDescription;
    });

    const distances = computed<Distance[] | undefined>(() =>
      getDistances(activityStaticData.value)
    );

    const activityTicketsLowestPrice = computed<Money | undefined>(() => {
      if (!activityStaticData.value) {
        return;
      }
      const startDate = new SemboDate(activityStaticData.value.ingredientDate);
      const endDate = new SemboDate(
        activityStaticData.value.ingredientDate
      ).add({
        days: activityStaticData.value.numberOfNights,
      });

      const ticketsInSelectedDates =
        activityDynamicData.value?.availability.filter(
          (dayTicket) =>
            (startDate.isBefore(new Date(dayTicket.date)) ||
              startDate.isSameDay(new Date(dayTicket.date))) &&
            (endDate.isAfter(new Date(dayTicket.date)) ||
              endDate.isSame(new Date(dayTicket.date)))
        );
      if (!ticketsInSelectedDates?.length) {
        return;
      }
      const prices = ticketsInSelectedDates.flatMap((dayTicket) =>
        dayTicket.rates.flatMap((rate) =>
          rate.ageRanges.map((ageRange) => ageRange.price)
        )
      );
      if (!prices || !prices.length) {
        return;
      }

      const nonZeroPrices = prices.filter((price) => price.amount > 0);

      if (!nonZeroPrices.length) {
        return;
      }

      const lowestPrice = nonZeroPrices.reduce((min, price) =>
        price.amount < min.amount ? price : min
      );
      return lowestPrice;
    });

    const selectedActivityTicketsInfo = computed<SelectedActivityTicketsInfo>(
      () => {
        const NO_SELECTED_TICKETS: SelectedActivityTicketsInfo = {
          selectedTicketTypes: [],
          totalPrice: undefined,
        };
        if (
          !recipeStore.activityOnlyRecipe.length ||
          !activityDynamicData.value ||
          !activityStaticData.value
        ) {
          return NO_SELECTED_TICKETS;
        }

        const activityIngredient = recipeStore.activityOnlyRecipe.find(
          (activity) =>
            activity.code === activityStaticData.value?.productCode &&
            activity.ingredientId === activityStaticData.value.ingredientId
        );

        if (!activityIngredient) {
          return NO_SELECTED_TICKETS;
        }

        const ingredientDate = new SemboDate(
          activityStaticData.value.ingredientDate
        );
        const offsetDate = ingredientDate.add({
          days: activityIngredient.offset,
        });
        const formattedDate = offsetDate.format(
          DateFormat.DateWithFullMonthComma
        );
        const offsetDateISO = offsetDate.format(DateFormat.ISO);

        const selectedActivityTicketsInfo =
          activityDynamicData.value.availability.find(
            (availabilityInDate) => availabilityInDate.date === offsetDateISO
          )?.rates[0];

        const hotelIngredients = tripStore.chronologicalItinerary
          .flatMap((itinerary) => itinerary.ingredients)
          .filter((ingredient) =>
            isAvailableHotelIngredientResponseWithStaticInfo(ingredient)
          ) as AvailableHotelIngredientResponseWithStaticInfo[];

        const itineraryActivityIngredient = hotelIngredients.flatMap((hotel) =>
          hotel.subIngredientResponseWithStaticInfo.filter(
            (subIngredient) =>
              subIngredient.suggested?.code ===
                activityStaticData.value?.productCode &&
              new SemboDate(subIngredient.date).format(DateFormat.ISO) ===
                offsetDateISO
          )
        )[0]?.suggested;

        if (
          !itineraryActivityIngredient ||
          !itineraryActivityIngredient.totalPrice
        ) {
          return NO_SELECTED_TICKETS;
        }

        const selectedTicketTypes: SelectedTickets[] =
          activityIngredient.tickets.map((ticket, index) => ({
            amount:
              itineraryActivityIngredient?.tickets[index]?.quantity ??
              ticket.quantity,
            date: formattedDate,
            time:
              itineraryActivityIngredient?.tickets[index]?.timeSlotId ??
              ticket.timeSlotId,
            ticketRate:
              itineraryActivityIngredient?.tickets[0]?.description ??
              selectedActivityTicketsInfo?.ageRanges.find(
                (ageRange) => ageRange.ageRangeId === ticket.ageRangeId
              )?.description,
          }));

        const result: SelectedActivityTicketsInfo = {
          selectedTicketTypes,
          totalPrice: itineraryActivityIngredient?.totalPrice,
        };

        return result;
      }
    );

    return {
      // State
      activityStaticData,
      activityDynamicData,

      // Actions
      setActivityStaticData,
      setActivityDynamicData,

      // Getters
      isLoadingTickets,
      isLoading,
      destinationCity,
      galleryData,
      mainData,
      description,
      distances,
      activityTicketsLowestPrice,
      selectedActivityTicketsInfo,
    };
  }
);
